/*
  This example requires some changes to your config:
  
  ```
  // tailwind.config.js
  module.exports = {
    // ...
    plugins: [
      // ...
      require('@tailwindcss/forms'),
    ],
  }
  ```
*/
import {Fragment, useEffect, useState} from 'react'
import { Dialog, Menu, Transition } from '@headlessui/react'
import {
  Bars3Icon,
  BellIcon,
  CalendarIcon,
  ChartPieIcon,
  ClipboardDocumentCheckIcon,
  Cog6ToothIcon,
  ComputerDesktopIcon,
  CurrencyDollarIcon,
  DocumentDuplicateIcon,
  FolderIcon,
  HomeIcon,
  UsersIcon,
  XMarkIcon,
  ChevronRightIcon,
  EyeSlashIcon,
  PhotoIcon,
  QueueListIcon,
  RectangleGroupIcon,
} from '@heroicons/react/24/outline'
import {ChevronDownIcon, EyeIcon, MagnifyingGlassIcon, UserIcon} from '@heroicons/react/20/solid'
import { getDatabase, ref, child, get } from 'firebase/database';
import ViewEval from "./view_eval/ViewEval";
import {auth} from "./firebase";
import { signOut } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { BuildingOfficeIcon } from '@heroicons/react/24/solid';
import { useLocation, useNavigate } from 'react-router-dom';
import useUnscoredReportCount from './report_queue/useUnscoredReportCount';

/**
 * Database used to query customers and eval data.
 * @type {Database}
 */
const navigation = [
  // { name: 'Dashboard', href: '#', icon: HomeIcon, current: true },
  // { name: 'Customers', href: '#', icon: UsersIcon, current: false },
  { name: 'Evals', href: '#', icon: FolderIcon, current: false },
  { name: 'Report', href: '/report', icon: ClipboardDocumentCheckIcon, current: false, badge: 'unscored' },
  { name: 'Evals', href: '/evals', icon: EyeIcon, current: false },
  { name: 'Cloud Evals', href: '/cloud-evals', icon: EyeSlashIcon, current: false },
  { name: 'Organizations', href: '/organizations', icon: BuildingOfficeIcon, current: false },
  { name: 'Stimuli', href: '/stimuli', icon: PhotoIcon, current: false },
  { name: 'Control Slides', href: '/control-slides', icon: RectangleGroupIcon, current: false },
  { name: 'Label Queue', href: '/stimuli/queue', icon: QueueListIcon, current: false },
  { name: 'Releases', href: '/releases', icon: ComputerDesktopIcon, current: false },
  { name: 'Billing', href: '/billing', icon: CurrencyDollarIcon, current: false }
]

const userNavigation = [
  { name: 'Your profile', href: '#' },
  { name: 'Sign out', href: '#' },
]

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

/**
 * VasiAdmin will render the user interface.
 * @param user
 * @param children
 * @returns {JSX.Element}
 * @constructor
 */
export default function VasiAdmin({user, children}) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const unscoredCount = useUnscoredReportCount();

  function isCurrent(item) {
    return item.href !== '#'
      && (location.pathname === item.href || location.pathname.startsWith(`${item.href}/`));
  }

  function navBadge(item) {
    if (item.badge !== 'unscored' || !unscoredCount) return null;
    return (
      <span
        className="ml-auto w-9 min-w-max whitespace-nowrap rounded-full bg-emerald-600 px-2.5 py-0.5 text-center text-xs font-medium leading-5 text-white"
        aria-label={`${unscoredCount} unscored evals`}
      >
        {unscoredCount}
      </span>
    );
  }

  async function handleSignOut() {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }

  return (
    <>
      <div>
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-900/80" />
            </Transition.Child>

            <div className="fixed inset-0 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                      <button type="button" className="-m-2.5 p-2.5" onClick={() => setSidebarOpen(false)}>
                        <span className="sr-only">Close sidebar</span>
                        <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                      </button>
                    </div>
                  </Transition.Child>
                  {/* Sidebar component, swap this element with another sidebar if you like */}
                  <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-900 px-6 pb-4 ring-1 ring-white/10">
                    <div className="flex h-16 shrink-0 items-center">
                      <img
                        className="h-8 w-auto"
                        src="https://hemispheresneuro.com/images/HNLogoDraft8Green.png"
                        alt="Your Company"
                      />
                    </div>
                    <nav className="flex flex-1 flex-col">
                      <ul role="list" className="flex flex-1 flex-col gap-y-7">
                        <li>
                          <ul role="list" className="-mx-2 space-y-1">
                            {navigation.map((item) => (
                              <li key={`${item.name}-${item.href}`}>
                                <a
                                  href={item.href}
                                  className={classNames(
                                    isCurrent(item)
                                      ? 'bg-gray-800 text-white'
                                      : 'text-gray-400 hover:text-white hover:bg-gray-800',
                                    'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                                  )}
                                >
                                  <item.icon className="h-6 w-6 shrink-0" aria-hidden="true" />
                                  {item.name}
                                  {navBadge(item)}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </li>
                        <li className="mt-auto">
                          <a
                            href="#"
                            className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-400 hover:bg-gray-800 hover:text-white"
                          >
                            <Cog6ToothIcon className="h-6 w-6 shrink-0" aria-hidden="true" />
                            Settings
                          </a>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Static sidebar for desktop */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-900 px-6 pb-4">
            <div className="flex h-16 shrink-0 items-center">
              <img
                className="h-8 w-auto"
                src="https://hemispheresneuro.com/images/HNLogoDraft8Green.png"
                alt="Your Company"
              />
              <div className="text-lg text-white ltr ps-4">VASI Admin</div>
            </div>
            <nav className="flex flex-1 flex-col">
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="-mx-2 space-y-1">
                    {navigation.map((item) => (
                      <li key={`${item.name}-${item.href}`}>
                        <a
                          href={item.href}
                          className={classNames(
                            isCurrent(item)
                              ? 'bg-gray-800 text-white'
                              : 'text-gray-400 hover:text-white hover:bg-gray-800',
                            'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                          )}
                        >
                          <item.icon className="h-6 w-6 shrink-0" aria-hidden="true" />
                          {item.name}
                          {navBadge(item)}
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>

                <li className="mt-auto">
                  <a
                    href="#"
                    className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-400 hover:bg-gray-800 hover:text-white"
                  >
                    <Cog6ToothIcon className="h-6 w-6 shrink-0" aria-hidden="true" />
                    Settings
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </div>

        <div className="lg:pl-72">
          <main className="py-10">
            <div className="px-4 sm:px-6 lg:px-8">
              { children }
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
